import { Component, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CmaState } from '../cma.state';

@Component({
  selector: 'app-view-cma-trends',
  templateUrl: './view-cma-trends.component.html',
  styleUrls: ['./view-cma-trends.component.scss'],
})
export class ViewCmaTrendsComponent implements OnInit {
  onDestroyNotifier$ = new Subject();
  Highcharts: typeof Highcharts = Highcharts;
  medianListPriceChart: Highcharts.Options = {};
  medianSoldPriceChart: Highcharts.Options = {};
  averageDOMChart: Highcharts.Options = {};
  medianSoldPricePerSqFeetChart: Highcharts.Options = {};
  inventoryChart: Highcharts.Options = {};
  medianListPriceVsMedianSoldPriceChart: Highcharts.Options = {};
  inventoryVsAbsorptionChart: Highcharts.Options = {};

  propertyTypes = [
    { key: 'SingleFamily', name: 'Single Family' },
    { key: 'TownHouse', name: 'Town Homes' },
    { key: 'Condominium', name: 'Condos' },
    { key: 'All', name: 'All Property Type' },
  ];
  propertySubTypeKeyMap: { [key: string]: string } = {
    'Single Family Residence': 'SingleFamily',
    Townhouse: 'TownHouse',
    Condominium: 'Condominium',
    Residential: 'Residential',
    Multifamily: 'Multifamily',
    Apartment: 'Apartment',
    'Non-Residential': 'NonResidential',
    All: 'All',
  };
  medianData = [
    {
      key: 'MedianListPrice',
      name: 'Median List Price',
    },
    {
      key: 'MedianSoldPrice',
      name: 'Median Sold Price',
    },
  ];
  inventoryAndAbsorptionInfo = [
    {
      key: 'ForSaleCount',
      name: 'Active Listings',
    },
    {
      key: 'AbsorptionRate',
      name: 'Sold Listings',
    },
  ];
  selectedPropertyKey: any;
  marketInsights: any;
  medianListPriceData = [];
  medianListPriceXaxis: any = {};
  medianSoldPriceData = [];
  medianSoldPriceXaxis: any = {};
  avgDomByProperty = [];
  avgDomXaxis: any = {};
  medianSoldPricePerSqFeetData = [];
  medianSoldPricePerSqFeetDataXaxis: any = {};
  inventoryData = [];
  inventoryDataXaxis: any = {};
  medianListandSoldPriceData = [];
  medianListandSoldPriceDataXaxis: any = {};
  inventoryAndAbsorptionDataXaxis: any = {};
  inventoryAndAbsorptionDataForChart = [];
  latestData: any;
  formattedMonths: any = [];

  constructor(private cmaState: CmaState) {}

  ngOnInit(): void {
    this.cmaState.cmaReports.pipe(takeUntil(this.onDestroyNotifier$)).subscribe((res: any) => {
      if (res && res.compReportData) {
        this.marketInsights = res.compReportData?.valueReport?.marketInsights.analysis;
        let propertySubtype = res.compReportData?.valueReport?.subject?.PropertySubType;
        this.selectedPropertyKey = this.propertySubTypeKeyMap[propertySubtype];
        if (this.marketInsights) {
          const dates = this.marketInsights.map((item) => item.month);
          const latestDate = dates.reduce((latest, current) => {
            return current > latest ? current : latest;
          }, '');
          this.latestData = this.marketInsights.filter((arr) => arr.month == latestDate);
        }
        this.createChart();
      }
    });
  }

  createChart() {
    this.formatDate();
    this.medianListPriceByProperty();
    this.medianSoldPriceByProperty();
    this.averageDOMByProperty();
    this.medianSoldPricePerSqFeetByProperty();
    this.inventoryByPropertyType();
    this.medianListandSoldPrice();
    this.inventoryAndAbsorptionData();
  }

  formatDate() {
    const months = this.marketInsights.map((item) => item.month).reverse();
    let lastYear = null;
    months.forEach((month) => {
      const [year, monthValue] = month.split('-');

      if (year !== lastYear) {
        this.formattedMonths.push(`${year}-${monthValue}`);
        lastYear = year;
      } else {
        this.formattedMonths.push(monthValue);
      }
    });
  }

  medianListPriceByProperty() {
    if (this.marketInsights) {
      this.medianListPriceData = this.propertyTypes.map((property) => {
        const dataForProperty = this.marketInsights.map((item) => item.MedianListPrice[property.key]);
        return {
          name: property.name,
          data: dataForProperty.reverse(),
        };
      });
      this.medianListPriceXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  medianSoldPriceByProperty() {
    if (this.marketInsights) {
      this.medianSoldPriceData = this.propertyTypes.map((property) => {
        const dataForProperty = this.marketInsights.map((item) => item.MedianSoldPrice[property.key]);
        return {
          name: property.name,
          data: dataForProperty.reverse(),
        };
      });
      this.medianSoldPriceXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  averageDOMByProperty() {
    if (this.marketInsights) {
      this.avgDomByProperty = this.propertyTypes.map((property) => {
        const dataForProperty = this.marketInsights.map((item) => item.AvgDOM[property.key]);
        return {
          name: property.name,
          data: dataForProperty.reverse(),
        };
      });
      this.avgDomXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  medianSoldPricePerSqFeetByProperty() {
    if (this.marketInsights) {
      this.medianSoldPricePerSqFeetData = this.propertyTypes.map((property) => {
        const dataForProperty = this.marketInsights.map((item) => item.MedianSoldPricePerSqft[property.key]);
        return {
          name: property.name,
          data: dataForProperty.reverse(),
        };
      });
      this.medianSoldPricePerSqFeetDataXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  inventoryByPropertyType() {
    if (this.marketInsights) {
      this.inventoryData = this.propertyTypes.map((property) => {
        const dataForProperty = this.marketInsights.map((item) => item.ForSaleCount[property.key]);
        return {
          name: property.name,
          data: dataForProperty.reverse(),
        };
      });
      this.inventoryDataXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  medianListandSoldPrice() {
    if (this.marketInsights) {
      this.medianListandSoldPriceData = this.medianData.map((data) => {
        const dataForProperty = this.marketInsights.map((item) => item[data.key]['All']);
        return {
          name: data.name,
          data: dataForProperty.reverse(),
        };
      });
      this.medianListandSoldPriceDataXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  inventoryAndAbsorptionData() {
    if (this.marketInsights) {
      this.inventoryAndAbsorptionDataForChart = this.inventoryAndAbsorptionInfo.map((data) => {
        const dataForProperty = this.marketInsights.map((item) => item[data.key]['All']);
        return {
          name: data.name,
          data: dataForProperty.reverse(),
        };
      });
      this.inventoryAndAbsorptionDataXaxis = {
        xAxis: {
          categories: this.formattedMonths,
        },
        yAxis: {
          title: {
            text: '',
          },
        },
        title: {
          text: '',
          align: 'left',
        },
        subtitle: {
          text: '',
          align: 'left',
        },
      };
    }
    this.createHighchartOption();
  }

  createHighchartOption() {
    this.medianListPriceChart = {
      title: this.medianListPriceXaxis.title,
      subtitle: this.medianListPriceXaxis.subtitle,
      yAxis: this.getYAxisWithFormatter(this.medianListPriceXaxis.yAxis),
      xAxis: this.medianListPriceXaxis.xAxis,
      series: this.medianListPriceData,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    this.medianSoldPriceChart = {
      title: this.medianSoldPriceXaxis.title,
      subtitle: this.medianSoldPriceXaxis.subtitle,
      yAxis: this.getYAxisWithFormatter(this.medianSoldPriceXaxis.yAxis),
      xAxis: this.medianSoldPriceXaxis.xAxis,
      series: this.medianSoldPriceData,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    this.averageDOMChart = {
      title: this.avgDomXaxis.title,
      subtitle: this.avgDomXaxis.subtitle,
      yAxis: this.avgDomXaxis.yAxis,
      xAxis: this.avgDomXaxis.xAxis,
      series: this.avgDomByProperty,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    this.medianSoldPricePerSqFeetChart = {
      title: this.medianSoldPricePerSqFeetDataXaxis.title,
      subtitle: this.medianSoldPricePerSqFeetDataXaxis.subtitle,
      yAxis: this.getYAxisWithFormatter(this.medianSoldPricePerSqFeetDataXaxis.yAxis),
      xAxis: this.medianSoldPricePerSqFeetDataXaxis.xAxis,
      series: this.medianSoldPricePerSqFeetData,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    this.inventoryChart = {
      title: this.inventoryDataXaxis.title,
      subtitle: this.inventoryDataXaxis.subtitle,
      yAxis: this.inventoryDataXaxis.yAxis,
      xAxis: this.inventoryDataXaxis.xAxis,
      series: this.inventoryData,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    this.medianListPriceVsMedianSoldPriceChart = {
      title: this.medianListandSoldPriceDataXaxis.title,
      subtitle: this.medianListandSoldPriceDataXaxis.subtitle,
      yAxis: this.getYAxisWithFormatter(this.medianListandSoldPriceDataXaxis.yAxis),
      xAxis: this.medianListandSoldPriceDataXaxis.xAxis,
      series: this.medianListandSoldPriceData,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    this.inventoryVsAbsorptionChart = {
      title: this.inventoryAndAbsorptionDataXaxis.title,
      subtitle: this.inventoryAndAbsorptionDataXaxis.subtitle,
      yAxis: this.inventoryAndAbsorptionDataXaxis.yAxis,
      xAxis: this.inventoryAndAbsorptionDataXaxis.xAxis,
      series: this.inventoryAndAbsorptionDataForChart,
      credits: {
        enabled: false, // <-- disables the Highcharts watermark
      },
    };
    Highcharts.chart('mediaListPriceContainer', this.medianListPriceChart);
    Highcharts.chart('mediaSoldPriceContainer', this.medianSoldPriceChart);
    Highcharts.chart('averageDOMContainer', this.averageDOMChart);
    Highcharts.chart('medianSoldPricePerSqFeetContainer', this.medianSoldPricePerSqFeetChart);
    Highcharts.chart('inventoryContainer', this.inventoryChart);
    Highcharts.chart('medianListPriceVsMedianSoldPriceContainer', this.medianListPriceVsMedianSoldPriceChart);
    Highcharts.chart('inventoryVsAbsorptionContainer', this.inventoryVsAbsorptionChart);
  }

  getYAxisWithFormatter(yAxis: Highcharts.YAxisOptions | undefined): Highcharts.YAxisOptions {
    const safeYAxis = yAxis || {};

    return {
      ...safeYAxis,
      labels: {
        ...(safeYAxis.labels || {}),
        formatter: function () {
          const val = Number(this.value);
          if (val >= 1000000) {
            const formatted = val / 1000000;
            return (
              '$' +
              (formatted % 1 === 0
                ? Highcharts.numberFormat(formatted, 0, '.', ',')
                : Highcharts.numberFormat(formatted, 1, '.', ',')) +
              'M'
            );
          } else if (val >= 1000) {
            const formatted = val / 1000;
            return '$' + Highcharts.numberFormat(formatted, formatted < 10 ? 1 : 0, '.', ',') + 'K';
          } else {
            return '$' + Highcharts.numberFormat(val, 0, '.', ',');
          }
        },
      },
    };
  }
}
